import { SignInButton } from '@clerk/react';
import type { FlowRoute, StepViewProps } from './flow-route-types.js';
import { stepNumberFor, totalSteps } from './gating.js';
import { Notice } from './notice.jsx';
import { normalizePath } from './router.js';
import { UnknownRoute } from './unknown-route.jsx';
import { useOnboarding } from './use-onboarding.jsx';
import { useSession } from './use-session.js';

export interface StepShellProps {
  route: FlowRoute;
  steps: readonly FlowRoute[];
  visiblePath: string;
  showErrors: boolean;
  onNavigate: (path: string) => void;
}

export function StepShell(props: StepShellProps) {
  return (
    <section className="card border border-base-300 bg-base-100 shadow-sm">
      <div className="card-body gap-0 p-4 sm:p-9">
        <div className="step-content-in" key={normalizePath(props.visiblePath)}>
          <StepBody {...props} />
        </div>
      </div>
    </section>
  );
}

function StepBody(props: StepShellProps) {
  const session = useSession();
  const { loadStatus, saveError } = useOnboarding();
  const isUnknownRoute = normalizePath(props.visiblePath) !== props.route.path;

  if (isUnknownRoute) {
    return <UnknownRoute visiblePath={props.visiblePath} onNavigate={props.onNavigate} />;
  }

  if (session.status === 'carregando') {
    return <LoadingBody route={props.route} steps={props.steps} message="Carregando…" />;
  }

  if (session.status === 'anonimo' && !props.route.publicWhenSignedOut) {
    return <SignedOutBody route={props.route} steps={props.steps} />;
  }

  if (session.status === 'autenticado' && loadStatus === 'carregando') {
    return <LoadingBody route={props.route} steps={props.steps} message="Carregando os dados da sua loja…" />;
  }

  if (session.status === 'autenticado' && loadStatus === 'erro') {
    return <LoadErrorBody route={props.route} steps={props.steps} />;
  }

  return (
    <>
      <StepHeading route={props.route} steps={props.steps} />
      <SaveErrorNotice message={saveError} />
      <props.route.View
        route={props.route}
        steps={props.steps}
        onNavigate={props.onNavigate}
        showErrors={props.showErrors}
      />
    </>
  );
}

export function StepHeading({ route, steps }: { route: FlowRoute; steps: readonly FlowRoute[] }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        {progressText(route, steps)}
      </p>
      <h1 className="mt-2 text-2xl font-semibold leading-tight sm:text-3xl">{route.title}</h1>
      <p className="mt-3 max-w-2xl opacity-70">{route.description}</p>
    </div>
  );
}

/** Derived from the same source as the rail caption, so the two cannot disagree. */
function progressText(route: FlowRoute, steps: readonly FlowRoute[]): string {
  if (route.kind === 'dashboard') {
    return 'Painel da loja';
  }

  if (route.kind === 'entry') {
    return 'Boas-vindas';
  }

  return `Etapa ${stepNumberFor(steps, route)} de ${totalSteps(steps)}`;
}

function LoadingBody({
  route,
  steps,
  message,
}: {
  route: FlowRoute;
  steps: readonly FlowRoute[];
  message: string;
}) {
  return (
    <>
      <StepHeading route={route} steps={steps} />
      <p className="flex items-center gap-3 opacity-70" role="status">
        <span className="loading loading-spinner loading-sm" aria-hidden="true" />
        {message}
      </p>
    </>
  );
}

function LoadErrorBody({ route, steps }: { route: FlowRoute; steps: readonly FlowRoute[] }) {
  const { reload } = useOnboarding();

  return (
    <>
      <StepHeading route={route} steps={steps} />
      <Notice tone="danger" title="Não foi possível carregar sua área da loja">
        Verifique sua conexão e tente novamente.
      </Notice>
      <div className="mt-6">
        <button className="btn btn-primary" type="button" onClick={reload}>
          Tentar novamente
        </button>
      </div>
    </>
  );
}

function SignedOutBody({ route, steps }: { route: FlowRoute; steps: readonly FlowRoute[] }) {
  return (
    <>
      <StepHeading route={route} steps={steps} />
      <Notice tone="info" title="Entre para continuar">
        Suas respostas ficam salvas na sua conta. Você pode sair e voltar quando quiser.
      </Notice>
      <div className="mt-6">
        <SignInButton mode="modal">
          <button className="btn btn-primary" type="button">
            Entrar na área da loja
          </button>
        </SignInButton>
      </div>
    </>
  );
}

function SaveErrorNotice({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <div className="mb-6">
      <Notice tone="danger" title="Alterações não salvas">
        {message}
      </Notice>
    </div>
  );
}

export type { StepViewProps };
